import React, { useState, useEffect } from 'react'
import { Table, FormControl, Button, OverlayTrigger, Tooltip, Collapse } from 'react-bootstrap'
import 'bootstrap-daterangepicker/daterangepicker.css';
import moment from 'moment'
import 'moment/locale/de'

import '../../../index.css'
import { FetchReadIncomingTransactions, FetchReadOutgoingTransactions, FetchDeleteTransactionDraft, FetchArticleTypeImages } from '../../../fetcher';
import { getSizeEntries, getSizeNumbers, getTotal } from '../../../utils/tableparser'
import AlertMessage from '../../alertmessages/AlertMessageView';
import { DescriptionTooltipView } from '../../_reusedcomponents/DescriptionTooltipView'
import { RenderImage } from '../../images/ImageView'
import { parseFloatCurrency } from '../../../utils/formatting/floatnumberparsing';
import { ExportCSV } from '../../_reusedcomponents/ExportCSVView'
import { enumvaluetranslations } from '../../../utils/enumvaluetranslations';
import { getColourClassStatus } from '../../../utils/cssgetter';
import { useHandleAlertMessage } from '../../../utils/togglinghooks';
import { useExpandRows, useSortTransactions } from '../../../utils/tablehooks';
import { getCsvDataForAllTransactions, getCsvDataForSingleTransaction, getCsvFileName } from '../../../utils/csvdatagetter';
import { DateSelect, StateSelect, useDateFilter, useFilterTransactions, useSearchFilter, useSelectFilter } from '../../../utils/filterutils';
moment.locale('de')

/**
 * Container for transactions that are related directly to the stock due to production.
 * Shows transactions of type newArrivals.
 * @param {any} parentReload reloads parent component
 * @param {any} updateTransactionDraft reloads parent component
 */
const TransactionContainer = ({ parentReload, updateTransactionDraft }) => {
    const { showAlertMessage, openAlert, closeAlert } = useHandleAlertMessage()
    const { expandRow, getIsExpandedRow } = useExpandRows()

    const [incomingTransactions, setIncomingTransactions] = useState([])
    const [outgoingTransactions, setOutgoingTransactions] = useState([])
    const [allTransactions, setAllTransactions] = useState([])
    const [allFilteredTransactions, setAllFilteredTransactions] = useState([])
    const [allSortedTransactions, setAllSortedTransactions] = useState([])
    const [filterArticles, setFilterArticles] = useState(false)
    const toggleFilterArticles = () => {
        setFilterArticles(!filterArticles)
    }

    const [articleDataReady, setArticleDataToReady] = useState(false)
    const [filteredTransactions, setFilteredTransactions] = useState([])
    const [filterable, setFilterable] = useState(false)
    const { sortTransactions } = useSortTransactions(allTransactions, setAllSortedTransactions)
    const { searchInput, handleSearchInput } = useSearchFilter()
    const [statusSelect, handleStatusSelectChange] = useSelectFilter()
    const { dateSelect, handleDatePickerCallback, handleClearFilter } = useDateFilter()
    const { filterTransactionsBySearchInput, filterTransactionsAndArticlesBySearchInput, filterTransactionsByDateInput, filterTransactionsBySelectInput } = useFilterTransactions()

    const addingImages = (articleTypeImages) => {
        let copiedIncomingTransactions = [...incomingTransactions]
        copiedIncomingTransactions.concat([...outgoingTransactions])
        articleTypeImages.forEach(image => copiedIncomingTransactions.flatMap(x => x.Articles).filter(x => x.ArticleTypeSysId == image.ArticleTypeSysId).forEach(xfiltered => xfiltered.Image = 'data:image/png;base64,' + image.Data))
        setIncomingTransactions(copiedIncomingTransactions)
    }

    const handleSetIncomingTransactions = (transactions) => {
        setIncomingTransactions(transactions.filter(x => x.Type == "NewArrivals" || x.Type == "CorrectionForNewArrivals"))
    }

    const handleSetOutgoingTransactions = (transactions) => {
        setOutgoingTransactions(transactions.filter(x => x.Type == "NewArrivals" || x.Type == "CorrectionForNewArrivals"))
    }

    useEffect(() => {
        FetchReadIncomingTransactions(handleSetIncomingTransactions, openAlert, setArticleDataToReady)
        FetchReadOutgoingTransactions(handleSetOutgoingTransactions, openAlert, setArticleDataToReady)
    }, [parentReload])

    useEffect(() => {
        let filteredTransactions = [...incomingTransactions].concat([...outgoingTransactions])
        setFilteredTransactions(filteredTransactions)
    }, [incomingTransactions, outgoingTransactions])

    useEffect(() => {
        if (articleDataReady && allTransactions.length > 0) {
            FetchArticleTypeImages(allTransactions.flatMap(transaction => transaction.Articles).map(article => article.ArticleTypeSysId.toString()), addingImages, openAlert)
        }
    }, [articleDataReady])

    useEffect(() => {
        let transactions = incomingTransactions
        transactions.concat(outgoingTransactions)
        setAllTransactions([...incomingTransactions, ...outgoingTransactions])
        setArticleDataToReady(true)
    }, [incomingTransactions, outgoingTransactions])

    useEffect(() => {
        setAllFilteredTransactions([...allTransactions])
        setAllSortedTransactions([...allTransactions])
    }, [allTransactions])

    const takeSortedTransactions = () => {
        //Maybe this shouldn't be done in general, as there should be another option to deep clone an object!
        let deepCopy = JSON.parse(JSON.stringify(allSortedTransactions))
        return deepCopy
    }

    useEffect(() => {
        let filteredTransactions = filterArticles ? filterTransactionsAndArticlesBySearchInput(takeSortedTransactions(), searchInput) : filterTransactionsBySearchInput(allSortedTransactions, searchInput)
        filteredTransactions = filterTransactionsByDateInput(filteredTransactions, dateSelect)
        filteredTransactions = filterTransactionsBySelectInput(filteredTransactions, statusSelect)

        setAllFilteredTransactions(filteredTransactions)
    }, [searchInput, dateSelect, statusSelect, allSortedTransactions, filterArticles])

    const reload = () => {
        FetchReadIncomingTransactions(handleSetIncomingTransactions, openAlert, setArticleDataToReady)
        FetchReadOutgoingTransactions(handleSetOutgoingTransactions, openAlert, setArticleDataToReady)
    }

    const removeTransactionDraft = (e, sysId) => {
        e.preventDefault()
        FetchDeleteTransactionDraft(sysId, openAlert, reload)
    }

    const setFilterbar = () => {
        setFilterable(!filterable)
    }

    const getTotalTotal = (articleTypes) => {
        let numbersTotal = 0
        articleTypes.forEach(x => {
            let numbers = x.Sizes.map(entry => parseInt(entry.Amount))
            let total = 0
            numbers.map(number => total = parseInt(total) + parseInt(number))

            numbersTotal += total
        })

        return numbersTotal
    }

    const handleSortTransactions = (e, field) => {
        e.preventDefault()
        sortTransactions(field)
    }

    return (
        <>
            <div className="inline">
                <FormControl className="globalSearch" as="input" placeholder={window.i18n('search')} onChange={handleSearchInput}></FormControl>
                <Button variant="light" className="filter-button form-control" onClick={toggleFilterArticles}>{filterArticles ? window.i18n('filterTransactions') : window.i18n('filterArticles')}</Button>
                <Button variant="light" className="form filter-button form-control" onClick={setFilterbar}>&#8711; Filter</Button>
                {/*<ExportCSV className="" disabled={false} csvData={getCsvDataForAllTransactions(filteredTransactions)} fileName={"Bestandstransaktionen"} />*/}
                <div className="clear"></div>
            </div>

            <div className="tablet-transactions-scroll">

                <Table className="table-sm table-striped-immediate">
                    <thead className="sticky-table-header">
                        <th></th>
                        <th className="mainColumn">{window.i18n('creationDate')}
                            <a href="" className="sort-link" onClick={(e) => handleSortTransactions(e, 'CreationDate')}>&#8593; &#8595;</a>
                        </th>
                        <th className="mainColumn">{window.i18n('type')}
                            <a href="" className="sort-link" onClick={(e) => handleSortTransactions(e, 'Type')}>&#8593; &#8595;</a>
                        </th>
                        <th className="mainColumn">{window.i18n('state')}
                            <a href="" className="sort-link" onClick={(e) => handleSortTransactions(e, 'State')}>&#8593; &#8595;</a>
                        </th>
                        <th className="mainColumn">{window.i18n('remark')}
                            <a href="" className="sort-link" onClick={(e) => handleSortTransactions(e, 'Remark')}>&#8593; &#8595;</a>
                        </th>
                        <th></th>
                    </thead>
                    {filterable ? <thead>
                        <th></th>
                        <th>{DateSelect(handleDatePickerCallback, handleClearFilter, dateSelect)}</th>
                        <th></th>
                        <th>{StateSelect([...new Set(allTransactions.map(x => enumvaluetranslations(x.State)))], handleStatusSelectChange, statusSelect)}</th>
                        <th></th>
                        <th></th>
                    </thead> : null}
                    <tbody>
                        {allFilteredTransactions.map((transaction, index) =>
                            <>
                                <tr className="salesboutique-mainrow" onClick={(e) => expandRow(e, transaction.TransactionSysId)} aria-expanded={getIsExpandedRow(transaction.TransactionSysId)} aria-controls={transaction.TransactionSysId}>
                                    {getIsExpandedRow(transaction.TransactionSysId) ? <td className="expandIndicator">&#8722;</td> : <td className="expandIndicator">&#43;</td>}
                                    <td>{transaction.CreationDate}</td>
                                    <td>{enumvaluetranslations(transaction.Type)}</td>
                                    <td className={getColourClassStatus(transaction.State)}>{enumvaluetranslations(transaction.State)}</td>
                                    <OverlayTrigger trigger={["hover", "click"]} placement="top" overlay={DescriptionTooltipView(transaction.Remark)}>
                                        <td className="remark-td">{transaction.Remark}</td>
                                    </OverlayTrigger>
                                    <td></td>
                                    {transaction.IsDraft &&
                                        <td>
                                            <OverlayTrigger placement="bottom" overlay={<Tooltip> {window.i18n('editTransaction')} </Tooltip>}>
                                            <Button className="button-mar" size="sm" variant="secondary" onClick={(e) => updateTransactionDraft(e, transaction)}>&#9998;</Button>
                                            </OverlayTrigger>
                                            <OverlayTrigger className="remove-order-td" placement="bottom" overlay={<Tooltip> {window.i18n('deleteTransaction')} </Tooltip>}>
                                            <a className="remove-order" href="" onClick={(e) => removeTransactionDraft(e, transaction.TransactionSysId)} variant="secondary">&#10060;</a>
                                            </OverlayTrigger>
                                        </td>
                                    }
                                </tr>

                                
                                    <Collapse in={getIsExpandedRow(transaction.TransactionSysId)} timeout={30}>
                                        <td colSpan="6" className="td-nomargin">
                                        <div id={transaction.TransactionSysId} className="salesBoutique-child-div">
                                            {[...new Set(transaction.Articles.map(article => article.SizeCat))].map(sizeCat =>
                                                <>
                                                    <thead className="sticky-table-header">
                                                        <tr className="salesboutique-childheaderrow">
                                                            <th className="imageColumn"></th>
                                                            <th>{window.i18n('articleNumber')}
                                                        </th>
                                                            <th>{sizeCat}
                                                            </th>
                                                            <th>{window.i18n('colour')}
                                                        </th>
                                                            <th className="numberColumn">{window.i18n('salePriceAbb')}
                                                        </th>
                                                            {getSizeEntries(transaction.Articles.find(x => x.SizeCat === sizeCat).Sizes)}
                                                            <th className="numberColumn">Total
                                                        </th>
                                                            <th></th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {transaction.Articles.sort((a, b) => a.Name.localeCompare(b.Name))
                                                            .filter(x => x.SizeCat === sizeCat).map(article =>
                                                                <>
                                                                    <tr>
                                                                        <td className="imageColumn">{RenderImage(article.Image)}</td>
                                                                        <td>{article.ArticleIdFaex}</td>
                                                                        <td>{article.Name}</td>
                                                                        <td>{article.Colour}</td>
                                                                        <td className="numberColumn">{parseFloatCurrency(article.SalePrice)}</td>
                                                                        {getSizeNumbers(article.Sizes)}
                                                                        {getTotal(article.Sizes)}
                                                                    </tr>
                                                                </>
                                                        )}

                                                        <tr className="stockReport-row">
                                                            <td></td>
                                                            <td></td>
                                                            <td></td>
                                                            <td></td>
                                                            <td></td>
                                                            <td></td>
                                                            <td></td>
                                                            <td>{window.i18n('sum') + ":"}</td>
                                                            <td>{getTotalTotal(transaction.Articles)}</td>
                                                        </tr>
                                                    </tbody>
                                                </>
                                            )}
                                        </div>

                                        <tr>
                                            <td></td>
                                            <td><ExportCSV className="searchBar" disabled={false} csvData={getCsvDataForSingleTransaction(transaction.Articles, [...new Set(transaction.Articles.map(article => article.SizeCat))])} fileName={getCsvFileName(transaction.CreationDate, "_" + window.i18n('showStockTransactions') + "_", "")} isTransaction={true} /></td>
                                        </tr>

                                        </td>
                                    </Collapse>
                            </>
                        )}
                    </tbody>
                </Table>
                <AlertMessage showAlertMessage={showAlertMessage} closeAlert={closeAlert} />
            </div>
        </>
    );
}

export { TransactionContainer }